import React, { useState, useEffect } from 'react';
import { X, Upload } from 'lucide-react';
import axios from '../../api/axios';
import toast from 'react-hot-toast';

const MemberModal = ({ member, branches, roles, onClose }) => {
  const [formData, setFormData] = useState({
    employeeId: '',
    name: '',
    email: '',
    phone: '',
    password: '',
    role: 'TECHNICIAN',
    branchId: '',
    isActive: true,
    canLogin: false,
    profilePhoto: '',
    emiratesId: '',
    licenseNumber: '',
    emiratesIdDocument: '',
    licenseDocument: '',
    address: '',
    dateOfBirth: '',
    hireDate: '',
    salary: ''
  });
  const [loading, setLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState({});

  useEffect(() => {
    if (member) {
      setFormData({
        employeeId: member.employeeId || '',
        name: member.name || '',
        email: member.email || '',
        phone: member.phone || '',
        password: '', // Don't populate password for editing
        role: member.role || 'TECHNICIAN',
        branchId: member.branchId || '',
        isActive: member.isActive !== undefined ? member.isActive : true,
        canLogin: member.canLogin || false,
        profilePhoto: member.profilePhoto || '',
        emiratesId: member.emiratesId || '',
        licenseNumber: member.licenseNumber || '',
        emiratesIdDocument: member.emiratesIdDocument || '',
        licenseDocument: member.licenseDocument || '',
        address: member.address || '',
        dateOfBirth: member.dateOfBirth ? member.dateOfBirth.split('T')[0] : '',
        hireDate: member.hireDate ? member.hireDate.split('T')[0] : '',
        salary: member.salary || ''
      });
    }
  }, [member]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = { ...formData };
      
      // Don't send empty password for updates
      if (member && !submitData.password) {
        delete submitData.password;
      }

      let memberId;
      if (member) {
        await axios.put(`/api/members/${member.id}`, submitData);
        memberId = member.id;
      } else {
        const response = await axios.post('/api/members', submitData);
        memberId = response.data.id;
      }

      // Upload files if any are selected
      if (Object.keys(selectedFiles).length > 0) {
        const fileFormData = new FormData();
        Object.entries(selectedFiles).forEach(([fieldName, file]) => {
          fileFormData.append(fieldName, file);
        });
        
        await axios.post(`/api/members/${memberId}/documents`, fileFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      toast.success(member ? 'Member updated successfully' : 'Member created successfully');
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const [previewUrls, setPreviewUrls] = useState({});

  useEffect(() => {
    // Cleanup blob URLs on unmount
    return () => {
      Object.values(previewUrls).forEach(url => {
        if (url && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [previewUrls]);

  const handleFileUpload = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      // Revoke previous URL if exists
      if (previewUrls[fieldName] && previewUrls[fieldName].startsWith('blob:')) {
        URL.revokeObjectURL(previewUrls[fieldName]);
      }
      
      const url = URL.createObjectURL(file);
      setPreviewUrls(prev => ({ ...prev, [fieldName]: url }));
      setSelectedFiles(prev => ({ ...prev, [fieldName]: file }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {member ? 'Edit Member' : 'Add New Member'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Employee ID
              </label>
              <input
                type="text"
                name="employeeId"
                value={formData.employeeId}
                onChange={handleChange}
                placeholder="Auto-generated if empty"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="+971 512345678"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password {!member && '*'}
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required={!member}
                placeholder={member ? "Leave empty to keep current password" : ""}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>



            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role *
              </label>
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Role</option>
                <option value="ADMIN">Admin</option>
                <option value="MANAGER">Manager</option>
                <option value="TECHNICIAN">Technician</option>
                <option value="RECEPTIONIST">Receptionist</option>
                <option value="STOREKEEPER">Storekeeper</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Branch
              </label>
              <select
                name="branchId"
                value={formData.branchId}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Branch</option>
                {branches.map(branch => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date of Birth
              </label>
              <input
                type="date"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hire Date
              </label>
              <input
                type="date"
                name="hireDate"
                value={formData.hireDate}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Base Salary
              </label>
              <input
                type="number"
                name="baseSalary"
                value={formData.baseSalary || ''}
                onChange={handleChange}
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Allowance
              </label>
              <input
                type="number"
                name="allowance"
                value={formData.allowance || ''}
                onChange={handleChange}
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Deductions
              </label>
              <input
                type="number"
                name="deductions"
                value={formData.deductions || ''}
                onChange={handleChange}
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Emirates ID
              </label>
              <input
                type="text"
                name="emiratesId"
                value={formData.emiratesId}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                License Number
              </label>
              <input
                type="text"
                name="licenseNumber"
                value={formData.licenseNumber}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Address
            </label>
            <textarea
              name="address"
              value={formData.address}
              onChange={handleChange}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* File Uploads */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Profile Photo
              </label>
              <label className="flex items-center px-4 py-2 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50">
                <Upload className="w-4 h-4 mr-2" />
                Choose Photo
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e, 'profilePhoto')}
                  className="hidden"
                />
              </label>
              {previewUrls.profilePhoto && (
                <div className="mt-2">
                  <img
                    src={previewUrls.profilePhoto}
                    alt="Profile preview"
                    className="w-16 h-16 object-cover rounded-full border"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <p className="text-xs text-green-600 mt-1">Photo selected</p>
                </div>
              )}
              {formData.profilePhoto && !previewUrls.profilePhoto && (
                <div className="mt-2">
                  <img
                    src={formData.profilePhoto}
                    alt="Profile photo"
                    className="w-16 h-16 object-cover rounded-full border"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <p className="text-xs text-gray-600 mt-1">Photo uploaded</p>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Emirates ID Document
              </label>
              <label className="flex items-center px-4 py-2 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50">
                <Upload className="w-4 h-4 mr-2" />
                Choose File
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => handleFileUpload(e, 'emiratesIdDocument')}
                  className="hidden"
                />
              </label>
              {previewUrls.emiratesIdDocument && (
                <div className="mt-2">
                  <img
                    src={previewUrls.emiratesIdDocument}
                    alt="Emirates ID preview"
                    className="w-16 h-16 object-cover rounded border"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <p className="text-xs text-green-600 mt-1">File selected</p>
                </div>
              )}
              {formData.emiratesIdDocument && !previewUrls.emiratesIdDocument && (
                <p className="text-xs text-gray-600 mt-1">Document uploaded</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                License Document
              </label>
              <label className="flex items-center px-4 py-2 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50">
                <Upload className="w-4 h-4 mr-2" />
                Choose File
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => handleFileUpload(e, 'licenseDocument')}
                  className="hidden"
                />
              </label>
              {previewUrls.licenseDocument && (
                <div className="mt-2">
                  <img
                    src={previewUrls.licenseDocument}
                    alt="License preview"
                    className="w-16 h-16 object-cover rounded border"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <p className="text-xs text-green-600 mt-1">File selected</p>
                </div>
              )}
              {formData.licenseDocument && !previewUrls.licenseDocument && (
                <p className="text-xs text-gray-600 mt-1">Document uploaded</p>
              )}
            </div>
          </div>

          {/* Status Checkboxes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Active Member</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                name="canLogin"
                checked={formData.canLogin}
                onChange={handleChange}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Can Login to System</span>
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : (member ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MemberModal;